import { ContentCopy } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import * as React from "react";
import { getToken } from "../../../services/auth.service";
import SectionTitle from "../../../ui-components/SectionTitle";
import { useToast } from "../../../ui-components/ToastProvider";

type EvolutionConfig = {
  baseUrl: string;
  apiKey: string;
  instance: string;
  number?: string;
};

const normalizeBaseUrl = (value: string) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  const noTrailingSlash = trimmed.replace(/\/+$/, "");
  return noTrailingSlash;
};

const toDataUrlIfBase64 = (value: string) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("data:image/")) return trimmed;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }
  if (/^[a-zA-Z0-9+/=]+$/.test(trimmed) && trimmed.length > 200) {
    return `data:image/png;base64,${trimmed}`;
  }
  return null;
};

const extractConnectPayload = (data: any) => {
  const pairingCode =
    data?.pairingCode?.code ??
    data?.pairingCode ??
    data?.response?.pairingCode?.code ??
    data?.response?.pairingCode ??
    null;

  const qrRaw =
    data?.qrcode ??
    data?.qrCode ??
    data?.qr ??
    data?.code ??
    data?.response?.qrcode ??
    data?.response?.qrCode ??
    data?.response?.qr ??
    data?.response?.code ??
    null;

  const qrDataUrl = typeof qrRaw === "string" ? toDataUrlIfBase64(qrRaw) : null;
  const qrText =
    qrDataUrl || typeof qrRaw !== "string"
      ? null
      : String(qrRaw || "").trim() || null;

  return {
    qrDataUrl,
    qrText,
    pairingCode: pairingCode ? String(pairingCode) : null,
  };
};

export default function EvolutionManager() {
  const { showToast } = useToast();
  const envConfig = React.useMemo<EvolutionConfig>(() => {
    const baseUrl = normalizeBaseUrl(
      process.env.REACT_APP_EVOLUTION_BASE_URL ||
        process.env.REACT_APP_EVOLUTION_URL ||
        "",
    );
    const apiKey = (process.env.REACT_APP_EVOLUTION_API_KEY || "").trim();
    const instance = (
      process.env.REACT_APP_EVOLUTION_INSTANCE ||
      process.env.REACT_APP_EVOLUTION_INSTANCE_NAME ||
      ""
    ).trim();
    const number = (process.env.REACT_APP_EVOLUTION_NUMBER || "").trim();

    return { baseUrl, apiKey, instance, number };
  }, []);

  const [config, setConfig] = React.useState<EvolutionConfig>(envConfig);
  const [isLoadingConfig, setIsLoadingConfig] = React.useState(false);
  const [isSavingConfig, setIsSavingConfig] = React.useState(false);

  const [status, setStatus] = React.useState<any>(null);
  const [qrDataUrl, setQrDataUrl] = React.useState<string | null>(null);
  const [qrText, setQrText] = React.useState<string | null>(null);
  const [pairingCode, setPairingCode] = React.useState<string | null>(null);

  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [isDisconnecting, setIsDisconnecting] = React.useState(false);
  const [isConfiguringWebhook, setIsConfiguringWebhook] =
    React.useState(false);

  const isBusy =
    isRefreshing || isConnecting || isDisconnecting || isConfiguringWebhook;

  const loadConfigFromApi = React.useCallback(async () => {
    const apiUrl = (process.env.REACT_APP_API_URL || "").trim();
    if (!apiUrl) return;

    setIsLoadingConfig(true);
    try {
      const response = await fetch(`${apiUrl}/evolution-config`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        method: "GET",
      });

      const json = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          json?.message || json?.error || `Erro HTTP ${response.status}`;
        throw new Error(String(message));
      }

      if (!json) return;

      setConfig((prev) => ({
        ...prev,
        baseUrl: normalizeBaseUrl(String(json.baseUrl || "")),
        apiKey: String(json.apiKey || "").trim(),
        instance: String(json.instance || "").trim(),
      }));
    } catch (err: any) {
      showToast(
        err?.message
          ? String(err.message)
          : "Falha ao carregar configuração do banco.",
        { severity: "error" },
      );
    } finally {
      setIsLoadingConfig(false);
    }
  }, [showToast]);

  const saveConfigToApi = React.useCallback(async () => {
    const apiUrl = (process.env.REACT_APP_API_URL || "").trim();
    if (!apiUrl) throw new Error("REACT_APP_API_URL não configurado.");

    const body = {
      baseUrl: normalizeBaseUrl(config.baseUrl),
      apiKey: String(config.apiKey || "").trim(),
      instance: String(config.instance || "").trim(),
    };

    if (!body.baseUrl) throw new Error("Informe a URL do Evolution.");
    if (!body.apiKey) throw new Error("Informe a API Key do Evolution.");
    if (!body.instance) throw new Error("Informe o nome da instância.");

    setIsSavingConfig(true);
    try {
      const response = await fetch(`${apiUrl}/evolution-config`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        method: "PUT",
        body: JSON.stringify(body),
      });

      const json = await response.json().catch(() => null);
      if (!response.ok) {
        const message =
          json?.message || json?.error || `Erro HTTP ${response.status}`;
        throw new Error(String(message));
      }

      showToast("Configuração salva no banco.", { severity: "success" });
      return json;
    } finally {
      setIsSavingConfig(false);
    }
  }, [config.apiKey, config.baseUrl, config.instance, showToast]);

  const evolutionRequest = React.useCallback(
    async (path: string, options?: RequestInit) => {
      const baseUrl = normalizeBaseUrl(config.baseUrl);
      if (!baseUrl) throw new Error("Informe a URL do Evolution.");
      if (!config.apiKey) throw new Error("Informe a API Key do Evolution.");
      if (!config.instance) throw new Error("Informe o nome da instância.");

      const headers = new Headers(options?.headers);
      headers.set("apikey", config.apiKey);
      if (options?.body && !headers.get("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }

      const response = await fetch(`${baseUrl}${path}`, {
        ...options,
        headers,
      });

      const text = await response.text();
      const json = text
        ? (() => {
            try {
              return JSON.parse(text);
            } catch {
              return { raw: text };
            }
          })()
        : null;

      if (!response.ok) {
        const message =
          json?.response?.message?.[0] ||
          json?.response?.message ||
          json?.message ||
          json?.error ||
          `Erro HTTP ${response.status}`;
        const error = new Error(String(message));
        (error as any).status = response.status;
        (error as any).payload = json;
        throw error;
      }

      return json;
    },
    [config.apiKey, config.baseUrl, config.instance],
  );

  const refreshStatus = React.useCallback(async () => {
    setIsRefreshing(true);
    try {
      const data = await evolutionRequest(
        `/instance/connectionState/${encodeURIComponent(config.instance)}`,
        { method: "GET" },
      );
      setStatus(data);
      if (data?.instance?.state === "open") {
        setQrDataUrl(null);
        setQrText(null);
        setPairingCode(null);
      }
      return data;
    } finally {
      setIsRefreshing(false);
    }
  }, [config.instance, evolutionRequest]);

  const ensureInstanceExists = React.useCallback(async () => {
    try {
      await refreshStatus();
      return;
    } catch (err: any) {
      if (err?.status !== 404) throw err;
    }

    const body: any = {
      instanceName: config.instance,
      qrcode: true,
    };
    if (config.number) body.number = config.number;

    const created = await evolutionRequest("/instance/create", {
      method: "POST",
      body: JSON.stringify(body),
    });
    void created;
  }, [config.instance, config.number, evolutionRequest, refreshStatus]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      await ensureInstanceExists();
      const url = config.number
        ? `/instance/connect/${encodeURIComponent(config.instance)}?number=${encodeURIComponent(
            config.number,
          )}`
        : `/instance/connect/${encodeURIComponent(config.instance)}`;

      const data = await evolutionRequest(url, { method: "GET" });

      const extracted = extractConnectPayload(data);
      setQrDataUrl(extracted.qrDataUrl);
      setQrText(extracted.qrText);
      setPairingCode(extracted.pairingCode);

      showToast("Solicitação de conexão enviada.", { severity: "success" });
      await refreshStatus();
    } catch (err: any) {
      showToast(
        err?.message ? String(err.message) : "Falha ao conectar no Evolution.",
        { severity: "error" },
      );
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const data = await evolutionRequest(
        `/instance/logout/${encodeURIComponent(config.instance)}`,
        { method: "DELETE" },
      );
      void data;
      showToast("Instância desconectada.", { severity: "success" });
      setQrDataUrl(null);
      setQrText(null);
      setPairingCode(null);
      await refreshStatus();
    } catch (err: any) {
      showToast(
        err?.message
          ? String(err.message)
          : "Falha ao desconectar no Evolution.",
        { severity: "error" },
      );
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleConfigureWebhook = async () => {
    const apiUrl = (process.env.REACT_APP_API_URL || "").trim();
    if (!apiUrl) {
      showToast("REACT_APP_API_URL não configurado.", { severity: "error" });
      return;
    }

    const webhookUrl = `${apiUrl.replace(/\/+$/, "")}/evolution-config/webhook`;

    setIsConfiguringWebhook(true);
    try {
      await evolutionRequest(
        `/webhook/set/${encodeURIComponent(config.instance)}`,
        {
          method: "POST",
          body: JSON.stringify({
            webhook: {
              enabled: true,
              url: webhookUrl,
              events: ["MESSAGES_UPSERT"],
            },
          }),
        },
      );
      showToast("Webhook configurado com sucesso.", { severity: "success" });
    } catch (err: any) {
      showToast(
        err?.message
          ? String(err.message)
          : "Falha ao configurar webhook no Evolution.",
        { severity: "error" },
      );
    } finally {
      setIsConfiguringWebhook(false);
    }
  };

  React.useEffect(() => {
    loadConfigFromApi().catch(() => {});
  }, [loadConfigFromApi]);

  React.useEffect(() => {
    if (!config.baseUrl || !config.apiKey || !config.instance) return;
    refreshStatus().catch(() => {});
  }, [config.apiKey, config.baseUrl, config.instance, refreshStatus]);

  React.useEffect(() => {
    if (
      status?.instance?.state !== "connecting" &&
      status?.instance?.state !== "qr"
    ) {
      return;
    }
    const id = window.setInterval(() => {
      refreshStatus().catch(() => {});
    }, 5000);
    return () => window.clearInterval(id);
  }, [refreshStatus, status?.instance?.state]);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showToast("Copiado.", { severity: "success" });
    } catch {
      showToast("Não foi possível copiar.", { severity: "error" });
    }
  };

  const stateLabel = status?.instance?.state
    ? String(status.instance.state)
    : "-";

  const stateChipColor =
    stateLabel === "open"
      ? "success"
      : stateLabel === "connecting" || stateLabel === "qr"
        ? "warning"
        : stateLabel === "close" || stateLabel === "closed"
          ? "error"
          : "default";

  return (
    <Grid sx={{ p: 2 }}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <SectionTitle title="Evolution (WhatsApp) — Instância" />
        <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
          A URL, API Key e o nome da instância são salvos no banco e usados para
          disparos de WhatsApp. Use os botões para conectar/desconectar e
          acompanhar o status.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Typography sx={{ fontWeight: 600, mb: 1, color: "#24324D" }}>
              Configuração
            </Typography>
            <TextField
              label="URL do Evolution"
              placeholder="https://evolution.seudominio.com"
              value={config.baseUrl}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, baseUrl: e.target.value }))
              }
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={isBusy || isLoadingConfig || isSavingConfig}
            />
            <TextField
              label="API Key"
              value={config.apiKey}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, apiKey: e.target.value }))
              }
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              type="password"
              disabled={isBusy || isLoadingConfig || isSavingConfig}
            />
            <TextField
              label="Instância"
              placeholder="minha-instancia"
              value={config.instance}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, instance: e.target.value }))
              }
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={isBusy || isLoadingConfig || isSavingConfig}
            />
            <TextField
              label="Número (opcional)"
              placeholder="5511999999999"
              value={config.number || ""}
              onChange={(e) =>
                setConfig((prev) => ({ ...prev, number: e.target.value }))
              }
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              disabled={isBusy || isLoadingConfig || isSavingConfig}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={() =>
                  saveConfigToApi().catch((err: any) => {
                    showToast(
                      err?.message
                        ? String(err.message)
                        : "Falha ao salvar configuração.",
                      { severity: "error" },
                    );
                  })
                }
                disabled={isBusy || isLoadingConfig || isSavingConfig}
                startIcon={
                  isSavingConfig ? <CircularProgress size={16} /> : undefined
                }
                sx={{ backgroundColor: "#24324D" }}
              >
                {isSavingConfig ? "Salvando..." : "Salvar"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => loadConfigFromApi().catch(() => {})}
                disabled={isBusy || isLoadingConfig || isSavingConfig}
                startIcon={
                  isLoadingConfig ? <CircularProgress size={16} /> : undefined
                }
              >
                {isLoadingConfig ? "Carregando..." : "Recarregar"}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography sx={{ fontWeight: 600, mb: 1, color: "#24324D" }}>
              Status e Ações
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
                mb: 2,
              }}
            >
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Instância
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>
                  {config.instance || "-"}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Estado
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={stateLabel}
                    size="small"
                    color={stateChipColor as any}
                    variant={
                      stateChipColor === "default" ? "outlined" : "filled"
                    }
                    sx={{ fontWeight: 700 }}
                  />
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    {stateLabel === "open"
                      ? "Conectado"
                      : stateLabel === "connecting" || stateLabel === "qr"
                        ? "Aguardando leitura"
                        : "Desconectado"}
                  </Typography>
                </Box>
              </Paper>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Button
                variant="contained"
                onClick={handleConnect}
                disabled={isBusy}
                startIcon={
                  isConnecting ? <CircularProgress size={16} /> : undefined
                }
                sx={{ backgroundColor: "#24324D" }}
              >
                {isConnecting ? "Conectando..." : "Conectar"}
              </Button>
              <Button
                variant="outlined"
                onClick={refreshStatus}
                disabled={isBusy}
                startIcon={
                  isRefreshing ? <CircularProgress size={16} /> : undefined
                }
              >
                {isRefreshing ? "Atualizando..." : "Atualizar status"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDisconnect}
                disabled={isBusy}
                startIcon={
                  isDisconnecting ? <CircularProgress size={16} /> : undefined
                }
              >
                {isDisconnecting ? "Desconectando..." : "Desconectar"}
              </Button>
              <Button
                variant="outlined"
                onClick={handleConfigureWebhook}
                disabled={isBusy}
                startIcon={
                  isConfiguringWebhook ? (
                    <CircularProgress size={16} />
                  ) : undefined
                }
              >
                {isConfiguringWebhook
                  ? "Configurando..."
                  : "Configurar Webhook de Mensagens"}
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: "#64748b", mb: 2, display: "block" }}>
              O webhook envia mensagens recebidas no WhatsApp para o backend, que
              cria automaticamente um lead no Kommo (sem duplicar) quando a
              mensagem for "Olá! Gostaria de mais informações sobre a Praceria.".
            </Typography>

            {(qrDataUrl || qrText || pairingCode) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography sx={{ fontWeight: 600, mb: 1, color: "#24324D" }}>
                  Conexão
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    background:
                      "linear-gradient(135deg, rgba(36,50,77,0.08) 0%, rgba(99,102,241,0.10) 50%, rgba(34,197,94,0.08) 100%)",
                    mb: 2,
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 700, color: "#24324D" }}>
                      Escaneie com o WhatsApp
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      WhatsApp → Dispositivos conectados → Conectar dispositivo
                    </Typography>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "360px 1fr" },
                        gap: 2,
                        mt: 2,
                        alignItems: "center",
                      }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "white",
                          border: "1px solid rgba(36,50,77,0.10)",
                          minHeight: 320,
                        }}
                      >
                        {qrDataUrl ? (
                          <img
                            src={qrDataUrl}
                            alt="QR Code do Evolution"
                            style={{
                              maxWidth: 300,
                              width: "100%",
                              borderRadius: 12,
                            }}
                          />
                        ) : qrText ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                            }}
                          >
                            <QRCodeCanvas
                              value={qrText}
                              size={280}
                              includeMargin
                              bgColor="#ffffff"
                              fgColor="#0f172a"
                            />
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: "#64748b" }}>
                            QR Code ainda não disponível.
                          </Typography>
                        )}
                      </Paper>

                      <Box>
                        {pairingCode && (
                          <Paper
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: "white",
                              border: "1px solid rgba(36,50,77,0.10)",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#64748b" }}
                                >
                                  Pairing code
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 800,
                                    letterSpacing: 1.2,
                                    fontSize: 18,
                                    color: "#0f172a",
                                  }}
                                >
                                  {pairingCode}
                                </Typography>
                              </Box>
                              <IconButton
                                onClick={() => handleCopy(pairingCode)}
                              >
                                <ContentCopy />
                              </IconButton>
                            </Box>
                          </Paper>
                        )}

                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: "white",
                            border: "1px solid rgba(36,50,77,0.10)",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "#64748b", mb: 1 }}
                          >
                            Dicas
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#334155" }}>
                            Se o QR não carregar, clique em “Conectar” novamente
                            ou “Atualizar status”.
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#334155", mt: 1 }}
                          >
                            Quando conectar, o estado muda para{" "}
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              open
                            </Box>
                            .
                          </Typography>
                        </Paper>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </>
            )}
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
}
